import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
    getAssignedTickets,
    getTicketById,
    addTicketMessage,
    updateTicketStatus,
    reopenTicket,
} from 'src/apis/support';
import { AddTicketMessageRequest, UpdateTicketStatusRequest } from 'types';
import { useToast } from 'src/context/ToastProvider';
import { get } from 'lodash';

export const useGetAssignedTickets = (status?: string) => {
    return useQuery(['getAssignedTickets', status], () => getAssignedTickets(status), {
        enabled: true,
        refetchInterval: 30000, // Refetch every 30 seconds for real-time updates
    });
};

export const useGetTicketById = (id: string) => {
    return useQuery(['getTicketById', id], () => getTicketById(id), {
        enabled: !!id,
        refetchInterval: 30000, // Refetch every 30 seconds for real-time updates
    });
};

export const useAddTicketMessage = () => {
    const queryClient = useQueryClient();
    const toast = useToast();

    return useMutation(
        ({ ticketId, data }: { ticketId: string; data: AddTicketMessageRequest }) =>
            addTicketMessage(ticketId, data),
        {
            onSuccess() {
                toast({
                    title: 'Message sent successfully',
                    type: 'success',
                });
                queryClient.refetchQueries(['getAssignedTickets']);
                queryClient.refetchQueries(['getTicketById']);
            },
            onError(error: any) {
                const errorMessage = get(error, ['response', 'data', 'message'], 'Failed to send message');
                toast({
                    title: errorMessage,
                    type: 'error',
                });
            },
        },
    );
};

export const useUpdateTicketStatus = () => {
    const queryClient = useQueryClient();
    const toast = useToast();

    return useMutation(
        ({ id, data }: { id: string; data: UpdateTicketStatusRequest }) => updateTicketStatus(id, data),
        {
            onSuccess() {
                toast({
                    title: 'Ticket status updated successfully',
                    type: 'success',
                });
                queryClient.refetchQueries(['getAssignedTickets']);
                queryClient.refetchQueries(['getTicketById']);
            },
            onError(error: any) {
                const errorMessage = get(error, ['response', 'data', 'message'], 'Failed to update ticket status');
                toast({
                    title: errorMessage,
                    type: 'error',
                });
            },
        },
    );
};

export const useReopenTicket = () => {
    const queryClient = useQueryClient();
    const toast = useToast();

    return useMutation(['reopenTicket'], reopenTicket, {
        onSuccess() {
            toast({
                title: 'Ticket reopened successfully',
                type: 'success',
            });
            queryClient.refetchQueries(['getAssignedTickets']);
            queryClient.refetchQueries(['getTicketById']);
        },
        onError(error: any) {
            const errorMessage = get(error, ['response', 'data', 'message'], 'Failed to reopen ticket');
            toast({
                title: errorMessage,
                type: 'error',
            });
        },
    });
};

